import {
  MODES,
  OPTIONS_FOR,
  buildContractorPayload,
  buildPayload,
  fieldsFor,
  isAvailable,
  isIncoming,
  round,
} from "@/components/ecologist/dataEntryNew/entryModes";

// Внесение данных по отходу (новое внесение данных): общее состояние этапов мастера

const SUBCLASSIFIERS = { pss17: 17, pss19: 19, pss20: 20, pss21: 21, pss22: 22 };

const initialState = () => ({
  // Узел отхода из дерева: techProcessHasWasteId, organizationWasteId, unitId, name
  waste: null,
  page: null,
  // Имя активного этапа мастера
  step: "mode",
  mode: null,
  // Дальнейшее действие после поступления
  action: null,
  dates: [],
  // Внесённые количества { [date]: "0.123" }
  amounts: {},
  // Поля строк по режиму и действию { [type]: { [date]: { ... } } }
  values: {},
  // Проверки дат на сервере { [date]: { problem, available, lastDate } }
  checks: {},
  options: {
    contractors: [],
    contractorsByDate: [],
    pss17: [],
    pss19: [],
    pss20: [],
    pss21: [],
    pss22: [],
    transferOrgs: [],
    storageOrgs: [],
  },
  loaded: {},
  optionsLoading: 0,
  showErrors: false,
  // Уже отправленные записи { "type:date": true } - повторно их не шлём
  sent: {},
  // Выполняемое действие (карточка или "save") - кнопка крутится, этапы не переключаются
  busy: null,
  // Предупреждение о перерасчёте за прошедшие даты показывается один раз
  recalcNoticed: false,
  // Дата строки, паспорт которой заполнили последним: следующий открывается его копией
  lastPassportDate: null,
});

// Идущие запросы проверки дат { [date]: Promise } - без реактивности, сбрасываются при смене отхода
let pendingChecks = {};

// Выполнить task для всех items, не больше limit запросов одновременно
async function runLimited(items, limit, task) {
  let index = 0;
  const worker = async () => {
    while (index < items.length) await task(items[index++]);
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker)
  );
}

// Контрагенты зависят от даты (действующие договоры) - перезагружаем при смене первой даты
const optionsKey = (state, name) =>
  name === "contractorsByDate" ? state.dates[0] : true;

function fetchOptions({ state, dispatch, rootGetters }, name) {
  const root = { root: true };
  if (SUBCLASSIFIERS[name])
    return dispatch(
      "getAllDataSubClassifierForSelect",
      { id: SUBCLASSIFIERS[name], size: 100, page: 0 },
      root
    ).then((response) => response.data.content);

  const params = {
    organizationWasteId: state.waste.organizationWasteId,
    unitId: state.waste.unitId,
  };
  if (name === "contractorsByDate")
    return dispatch("getContractorsByDate", state.dates[0], root).then((list) =>
      list.map((item) => ({
        id: item.contractorId,
        name: item.contractNumber
          ? `${item.nameManual} / ${item.contractNumber}`
          : item.nameManual,
      }))
    );
  if (name === "contractors")
    // undefined, а не null: у экшена параметры по умолчанию = {}, null их не подставляет
    return dispatch("getListContractors", undefined, root).then(
      () => rootGetters.GETLISTCONTRACTORSSELECT
    );
  if (name === "transferOrgs")
    return dispatch("getTransferOrganizationList", params, root).then(
      () => rootGetters.GETTRANSFERORGANIZATIONLIST
    );
  if (name === "storageOrgs")
    return dispatch("getSentForStorageOrganizationList", params, root).then(
      () => rootGetters.GETSENTFORSTORAGEORGANIZATIONLIST
    );
  throw new Error("Неизвестный справочник " + name);
}

// Как setDatastored в старых модалках: блок главного эколога, цепочка, остаток на дату
async function fetchCheckOnce({ state, commit, dispatch }, date) {
  const waste = state.waste;
  const [block, stored] = await Promise.all([
    dispatch("getBlockForUser", date, { root: true }),
    dispatch(
      "getDataStored",
      { date, techProcessHasWasteId: waste.techProcessHasWasteId },
      { root: true }
    ),
  ]);
  if (state.waste !== waste) return;
  const data = stored.data;
  let problem = null;
  if (block.data === true) problem = "Заблокировано главным экологом";
  else if (data === true) problem = "Цепочка устарела";
  else if (data === false) problem = "Цепочка не начала действовать";
  commit("setCheck", {
    date,
    check: {
      problem,
      available: problem ? null : data.amount - data.sumAmountGet,
      lastDate: problem ? null : data.path,
    },
  });
}

// Сбой сети или сервера на одной дате не должен валить проверку - повторяем один раз
async function fetchCheck(context, date, retry = true) {
  try {
    await fetchCheckOnce(context, date);
  } catch (error) {
    if (!retry) throw error;
    await new Promise((resolve) => setTimeout(resolve, 500));
    await fetchCheck(context, date, false);
  }
}

/*
  Уже идущую проверку даты не повторяем, а ждём. Завершённая убирается из идущих:
  удачная уже лежит в state.checks, а неудачную или не записанную (сменился отход)
  checkDates запросит снова - иначе у даты навсегда пропадает остаток
*/
function checkDate(context, date) {
  const checks = pendingChecks;
  if (!checks[date])
    checks[date] = fetchCheck(context, date).finally(() => {
      delete checks[date];
    });
  return checks[date];
}

const getters = {
  isIncoming: (state) => isIncoming(state.mode),
  // Пока режим не выбран - общее название этапа дат
  entryTitle: (state) =>
    state.mode ? MODES[state.mode].stepTitle : "Даты и количество",
  detailsTitle: (state) => (state.action ? MODES[state.action].stepTitle : ""),
  // Есть что терять при переключении на другой элемент дерева
  isDirty: (state) =>
    state.step !== "mode" || !!state.mode || state.dates.length > 0,
  // Есть что терять при смене режима
  hasEntered: (state) =>
    state.dates.length > 0 || Object.keys(state.amounts).length > 0,
  anySent: (state) => Object.keys(state.sent).length > 0,

  unitName: (state) =>
    state.page && state.page.pss93UnitInfo ? state.page.pss93UnitInfo.name : "",
  hasStorage: (state) => isAvailable("inStorage", state.page),
  // Как в dataEntryEdit: списывать можно, только если есть остаток
  noRemainder: (state) =>
    round(state.page.amount) - round(state.page.sumAmountGet) <= 0,
  /*
    Как в dataEntryEdit: накопленное для временного хранения против одной транспортной единицы.
    over - превышена на amount, reached - достигнута, near - больше 90%, осталось amount.
    Без лимита или ниже 90% - null
  */
  transportUnit: (state) => {
    const { sumAmount, storeLimit } = state.page || {};
    if (!(storeLimit > 0)) return null;
    const diff = Math.round((sumAmount - storeLimit) * 1000) / 1000;
    if (diff > 0) return { status: "over", amount: diff };
    if (diff === 0) return { status: "reached", amount: 0 };
    if (sumAmount > storeLimit * 0.9) return { status: "near", amount: -diff };
    return null;
  },

  // Этап дат показывает поля режима, этап действия - поля действия
  modeFields: (state) => fieldsFor(state.mode, state.options),
  actionFields: (state) => fieldsFor(state.action, state.options),
  /*
    Доступно на дату = остаток на эту дату с сервера минус количества строк выше:
    даты идут по возрастанию, и каждое списание уменьшает остаток на все следующие даты.
    Иначе несколько строк «по максимуму» вместе уводят остаток в минус.
    У поступления остаток не ограничивает - null
  */
  available: (state, getters) => {
    if (getters.isIncoming) return null;
    const first = state.dates.find((date) => state.amounts[date] !== undefined);
    const available = {};
    let spent = 0;
    state.dates.forEach((date) => {
      const check = state.checks[date];
      if (!check || check.available === null) return;
      available[date] = check.available - spent;
      // Как в таблице: своё количество или серое предложенное из первой внесённой строки
      const own =
        state.amounts[date] !== undefined
          ? state.amounts[date]
          : first && state.amounts[first];
      const amount = parseFloat(own);
      if (!isNaN(amount)) spent += amount;
    });
    return available;
  },
  dateProblems: (state) => {
    const problems = {};
    state.dates.forEach((date) => {
      const check = state.checks[date];
      if (check && check.problem) problems[date] = check.problem;
    });
    return problems;
  },
  // Внесение за прошедшие даты пересчитывает уже внесённое после них
  recalculates: (state) =>
    state.dates.some((date) => {
      const check = state.checks[date];
      return check && Date.parse(check.lastDate) > Date.parse(date);
    }),
};

const mutations = {
  reset(state, waste) {
    Object.assign(state, initialState(), { waste });
  },
  setPage(state, page) {
    state.page = page;
  },
  setStep(state, step) {
    state.step = step;
    state.showErrors = false;
  },
  // Смена режима стирает всё введённое: поля и проверки у режимов разные
  setMode(state, mode) {
    Object.assign(state, {
      mode,
      action: null,
      dates: [],
      amounts: {},
      values: {},
    });
  },
  // Поля прежнего действия стираются, поля режима остаются
  setAction(state, action) {
    state.action = action;
    state.values = { [state.mode]: state.values[state.mode] || {} };
  },
  setDates(state, dates) {
    state.dates = dates;
  },
  setAmounts(state, amounts) {
    state.amounts = amounts;
  },
  setValues(state, { type, values }) {
    state.values = { ...state.values, [type]: values };
  },
  setCheck(state, { date, check }) {
    state.checks = { ...state.checks, [date]: check };
  },
  setOptions(state, { name, list, key }) {
    state.options[name] = list;
    state.loaded[name] = key;
  },
  addOptionsLoading(state, delta) {
    state.optionsLoading += delta;
  },
  markSent(state, keys) {
    const sent = { ...state.sent };
    keys.forEach((key) => (sent[key] = true));
    state.sent = sent;
  },
  setBusy(state, busy) {
    state.busy = busy;
  },
  setShowErrors(state, value) {
    state.showErrors = value;
  },
  setRecalcNoticed(state) {
    state.recalcNoticed = true;
  },
  setLastPassportDate(state, date) {
    state.lastPassportDate = date;
  },
};

const actions = {
  // Начать внесение по отходу с чистого листа
  start({ commit }, waste) {
    pendingChecks = {};
    commit("reset", waste);
  },

  async loadPage({ state, commit, dispatch, rootGetters }) {
    const waste = state.waste;
    await dispatch("getMainPageForDataEntry", waste.techProcessHasWasteId, {
      root: true,
    });
    if (state.waste === waste) commit("setPage", rootGetters.GETDATAENTRYMAINPAGE);
  },

  // Возвращает загрузку справочников режима: при сбое она отклоняется
  changeMode({ commit, dispatch }, mode) {
    commit("setMode", mode);
    return dispatch("loadOptions", mode);
  },

  // Повторный выбор того же действия оставляет уже заполненные поля
  chooseAction({ state, commit, dispatch }, action) {
    if (action === state.action) return;
    commit("setAction", action);
    return dispatch("loadOptions", action);
  },

  // Справочники по первой дате ждут, пока даты выберут
  async loadOptions(context, type) {
    const { state, commit } = context;
    const waste = state.waste;
    const names = (OPTIONS_FOR[type] || []).filter((name) => {
      const key = optionsKey(state, name);
      return key !== undefined && state.loaded[name] !== key;
    });
    if (!names.length) return;
    commit("addOptionsLoading", 1);
    try {
      await Promise.all(
        names.map(async (name) => {
          const key = optionsKey(state, name);
          const list = (await fetchOptions(context, name)) || [];
          // Пока грузили, сменился отход или первая дата - список уже не тот
          if (state.waste === waste && optionsKey(state, name) === key)
            commit("setOptions", { name, list, key });
        })
      );
    } finally {
      commit("addOptionsLoading", -1);
    }
  },

  // Первая дата сменилась: справочники, зависящие от неё, - заново для режима и действия
  reloadOptions({ state, dispatch }) {
    return Promise.all([
      dispatch("loadOptions", state.mode),
      dispatch("loadOptions", state.action),
    ]);
  },

  // Проверить ещё не проверенные даты; при сбое отклоняется
  async checkDates(context) {
    const { state } = context;
    const missing = state.dates.filter((date) => !state.checks[date]);
    await runLimited(missing, 6, (date) => checkDate(context, date));
  },

  // Все записи режима одним запросом, в том числе строки с сопроводительным паспортом
  async send({ state, commit, dispatch }, { type, dates, values }) {
    const pending = dates.filter((date) => !state.sent[type + ":" + date]);
    if (!pending.length) return;
    await dispatch("loadPage");
    const { waste, page } = state;

    // Передача контрагенту - через /receive/formwaste, как «Вывоз отходов»
    if (type === "contractor") {
      const records = pending.map((date) =>
        buildContractorPayload({
          date,
          store: state.amounts[date],
          amount: page.amount,
          values: values[date] || {},
          tphwId: waste.techProcessHasWasteId,
        })
      );
      await dispatch("receiveContractorTransfer", records, { root: true });
    } else {
      const payloads = pending.map((date) =>
        buildPayload(type, {
          date,
          store: state.amounts[date],
          // Как в старых модалках: текущее количество на хранении
          amount: page.amount,
          values: values[date] || {},
          unitId: waste.unitId,
        })
      );
      await dispatch(
        "receiveDataEntryForm",
        {
          modalData: payloads,
          id: waste.techProcessHasWasteId,
          actionId: MODES[type].actionId,
        },
        { root: true }
      );
    }
    if (state.waste === waste)
      commit(
        "markSent",
        pending.map((date) => type + ":" + date)
      );
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};
